/**
 * Contrato del panel con `GET /api/v1/admin/roles/`.
 *
 * Este archivo es el UNICO lugar del panel que sabe como viene el JSON de roles.
 * Todo lo demas trabaja contra los tipos normalizados de aca, asi que si el
 * backend cambia una forma se toca este archivo y nada mas.
 *
 * Forma REAL que devuelve el endpoint (verificada contra la respuesta viva):
 *
 * ```jsonc
 * {
 *   "count": 34,
 *   "roles": [
 *     {
 *       "id": 3,                          // id interno del grupo; hace falta para ASIGNAR
 *       "code": "base.administrador",
 *       "name": "Administrador",
 *       "description": "Administra la organización...",
 *       "module": "base",
 *       "module_label": "Base",
 *       "user_count": 2,
 *       "areas": [
 *         { "module": "invoicing", "module_label": "Facturación",
 *           "models": [
 *             { "model": "invoicing.invoice", "label": "Factura de cliente",
 *               "label_plural": "Facturas de cliente",
 *               "can": { "create": true, "read": true, "write": true, "delete": false },
 *               "allowed": ["ver"], "denied": ["borrar"], "summary": "..." }
 *           ] }
 *       ],
 *       "record_scope": [
 *         { "model": "invoicing.invoice", "label": "Factura de cliente",
 *           "scope": "group",             // "group" | "global"
 *           "rule": "Sólo las de sus empresas",
 *           "translated": true,
 *           "text": "Sólo los registros donde «Empresa» está entre...",
 *           "domain": [["company", "in", "$company.ids"]] }
 *       ]
 *     }
 *   ]
 * }
 * ```
 *
 * Cuatro detalles que cuestan una pantalla en blanco si se leen mal, y que este
 * archivo resuelve:
 *   1. los permisos vienen ANIDADOS en `can`, no planos;
 *   2. el area se llama `module_label`, no `label`;
 *   3. las reglas de registro vienen en `record_scope`, no en `scopes`;
 *   4. `global` es `scope === "global"`, no un booleano.
 *
 * La normalizacion sigue siendo tolerante: acepta la lista en `roles`, en
 * `results` o pelada en la raiz, y acepta las capacidades tanto agrupadas en
 * `areas` como planas (`capabilities` / `permissions` / `accesses`), en cuyo
 * caso las agrupa por el prefijo del nombre logico del modelo.
 *
 * Lo que NO hace: inventar. Si un campo no viene, queda `null` o vacio y la
 * pantalla lo dice con todas las letras. Un panel de permisos que adivina es
 * peor que un panel de permisos que falta.
 */

import type { Domain } from '@/lib/types';

// ---------------------------------------------------------------------------
// Tipos normalizados
// ---------------------------------------------------------------------------
export type CrudVerb = 'create' | 'read' | 'write' | 'delete';

/** Las cuatro operaciones, en el orden en que se leen de menor a mayor riesgo. */
export const VERBOS: readonly CrudVerb[] = ['read', 'create', 'write', 'delete'] as const;

/** Que puede hacer un rol sobre UN modelo. */
export interface RoleCapability {
  /** Nombre logico: `invoicing.invoice`. */
  model: string;
  /** Etiqueta del modelo en castellano. Si el backend no la manda, cae al nombre logico. */
  label: string;
  /** `true` cuando la etiqueta es el nombre tecnico porque el backend no mando una. */
  labelIsTechnical: boolean;
  create: boolean;
  read: boolean;
  write: boolean;
  delete: boolean;
}

/** Un area funcional: normalmente un modulo del ERP. */
export interface RoleArea {
  key: string;
  label: string;
  capabilities: RoleCapability[];
}

/** Que FILAS ve el rol sobre un modelo (capa 2: reglas de registro). */
export interface RoleScope {
  model: string;
  label: string;
  /**
   * Nombre de la regla tal como la declaro el `security.py` del modulo. Es la
   * frase en castellano del oficio («Solo los gastos de mis edificios»), escrita
   * al lado del dominio que se aplica, y es lo que la ficha muestra primero: la
   * traduccion literal del dominio («Solo los registros donde «Periodo →
   * Edificio → Unidades funcionales → Propietario → Usuarios» es...») es exacta
   * pero no la puede evaluar quien tiene que decidir si da el rol.
   */
  rule: string;
  /** Frase en castellano. Vacia si el backend no la mando: entonces se muestra el dominio. */
  description: string;
  /** `true` = regla global (restriccion dura para todos), `false` = regla del grupo. */
  global: boolean;
  /** `true` si el dominio no filtra nada. El backend ya lo dice en `unrestricted`. */
  unrestricted: boolean;
  domain: Domain | null;
}

/**
 * Las reglas de UN modelo, agrupadas y con el conector que las une.
 *
 * Existe porque una lista plana de reglas MIENTE por omision: «Empresa: solo las
 * de tu organizacion» seguido de «Empresa: todos los registros del modelo» se lee
 * como si el segundo pisara al primero, y el motor las combina con AND. El
 * backend manda esto ya armado en `record_scope_by_model`.
 */
export interface RoleScopeGroup {
  model: string;
  label: string;
  /** Reglas globales: se cumplen TODAS a la vez. */
  hard: RoleScope[];
  /** Reglas del rol: alcanza con cumplir UNA. */
  group: RoleScope[];
  /** Encabezado del bloque duro, tal como lo redacto el backend. */
  hardTitle: string;
  /** Encabezado del bloque del rol. */
  groupTitle: string;
  /** Que sale de combinar las dos listas. Frase del backend, no deducida aca. */
  effect: string;
}

export interface Role {
  /** Id interno del grupo. `null` si el endpoint no lo expone: sin el no se puede asignar. */
  id: number | null;
  code: string;
  name: string;
  description: string;
  /** Modulo que declara el rol. `null` si no vino. */
  module: string | null;
  /** Cuanta gente lo tiene. `null` = el endpoint no lo informa (distinto de cero). */
  userCount: number | null;
  areas: RoleArea[];
  scopes: RoleScope[];
  /** Las mismas reglas agrupadas por modelo. Vacio si el backend no las agrupo. */
  scopeGroups: RoleScopeGroup[];
  /**
   * Lo que esta ficha NO puede describir: el superusuario saltea las dos capas.
   * Viene del backend porque es una afirmacion sobre el motor de seguridad, no
   * una redaccion de la pantalla. Vacio = el servidor no lo informo.
   */
  bypassNote: string;
}

/** Error de forma del contrato. Se muestra tal cual en la pantalla. */
export class ContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ContractError';
  }
}

// ---------------------------------------------------------------------------
// Lectura defensiva
// ---------------------------------------------------------------------------
function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

function leerTexto(fuente: Record<string, unknown>, ...claves: string[]): string {
  for (const clave of claves) {
    const valor = fuente[clave];
    if (typeof valor === 'string' && valor.trim() !== '') return valor.trim();
  }
  return '';
}

function leerBooleano(fuente: Record<string, unknown>, ...claves: string[]): boolean {
  for (const clave of claves) {
    const valor = fuente[clave];
    if (typeof valor === 'boolean') return valor;
  }
  return false;
}

/** Numero o `null`. `null` significa "no informado", nunca "cero". */
function leerNumero(fuente: Record<string, unknown>, ...claves: string[]): number | null {
  for (const clave of claves) {
    const valor = fuente[clave];
    if (typeof valor === 'number' && Number.isFinite(valor)) return valor;
    // Algunos serializadores mandan el conteo como lista de usuarios.
    if (Array.isArray(valor)) return valor.length;
  }
  return null;
}

function leerLista(fuente: Record<string, unknown>, ...claves: string[]): unknown[] {
  for (const clave of claves) {
    const valor = fuente[clave];
    if (Array.isArray(valor)) return valor;
  }
  return [];
}

/** El dominio viaja como lista; cualquier otra cosa se descarta en vez de romper. */
function leerDominio(fuente: Record<string, unknown>): Domain | null {
  const valor = fuente.domain;
  if (!Array.isArray(valor) || valor.length === 0) return null;
  return valor as Domain;
}

// ---------------------------------------------------------------------------
// Normalizacion
// ---------------------------------------------------------------------------
/** Prefijo del nombre logico: `invoicing.invoice` -> `invoicing`. */
export function moduloDeModelo(modelo: string): string {
  const punto = modelo.indexOf('.');
  return punto === -1 ? modelo : modelo.slice(0, punto);
}

function normalizarCapacidad(crudo: unknown): RoleCapability | null {
  if (!esObjeto(crudo)) return null;
  const model = leerTexto(crudo, 'model', 'model_name', 'name');
  if (model === '') return null;

  const label = leerTexto(crudo, 'label', 'model_label', 'verbose_name');
  // El backend manda los cuatro permisos ANIDADOS en `can`
  // (`{"can": {"create": false, "read": true, ...}}`). Se lee de ahi primero y
  // se cae a las claves planas, que es la forma que aceptaba este normalizador
  // antes de que el endpoint existiera.
  const can = esObjeto(crudo.can) ? crudo.can : crudo;
  return {
    model,
    label: label === '' ? model : label,
    labelIsTechnical: label === '',
    create: leerBooleano(can, 'create', 'can_create'),
    read: leerBooleano(can, 'read', 'can_read'),
    write: leerBooleano(can, 'write', 'can_write'),
    delete: leerBooleano(can, 'delete', 'can_delete'),
  };
}

/** Agrupa capacidades planas por el modulo del modelo, para cuando no vienen en `areas`. */
function agruparPorModulo(capacidades: RoleCapability[]): RoleArea[] {
  const porClave = new Map<string, RoleCapability[]>();
  for (const capacidad of capacidades) {
    const clave = moduloDeModelo(capacidad.model);
    const actual = porClave.get(clave);
    if (actual === undefined) porClave.set(clave, [capacidad]);
    else actual.push(capacidad);
  }
  return [...porClave.entries()].map(([key, lista]) => ({ key, label: '', capabilities: lista }));
}

function normalizarAreas(crudo: Record<string, unknown>): RoleArea[] {
  const areasCrudas = leerLista(crudo, 'areas', 'groups_by_area', 'by_area');
  if (areasCrudas.length > 0) {
    const areas: RoleArea[] = [];
    for (const area of areasCrudas) {
      if (!esObjeto(area)) continue;
      const key = leerTexto(area, 'key', 'module', 'name', 'category');
      const capacidades = leerLista(area, 'capabilities', 'permissions', 'accesses', 'models')
        .map(normalizarCapacidad)
        .filter((c): c is RoleCapability => c !== null);
      if (key === '' && capacidades.length === 0) continue;
      areas.push({
        key: key === '' ? 'otros' : key,
        // El endpoint nombra el area `module_label`, no `label`.
        label: leerTexto(area, 'module_label', 'label', 'title'),
        capabilities: capacidades,
      });
    }
    if (areas.length > 0) return areas;
  }

  // Sin `areas`: capacidades planas agrupadas por modulo.
  const planas = leerLista(crudo, 'capabilities', 'permissions', 'accesses', 'model_access')
    .map(normalizarCapacidad)
    .filter((c): c is RoleCapability => c !== null);
  return agruparPorModulo(planas);
}

function normalizarAlcance(item: unknown): RoleScope | null {
  if (!esObjeto(item)) return null;
  const model = leerTexto(item, 'model', 'model_name');
  if (model === '') return null;
  const label = leerTexto(item, 'label', 'model_label');
  return {
    model,
    label: label === '' ? model : label,
    rule: leerTexto(item, 'rule', 'rule_name'),
    // `text` viene VACIO a proposito cuando el backend no pudo traducir el
    // dominio (`translated: false`). En ese caso la pantalla muestra el
    // dominio crudo, que es lo correcto: no hay que inventar una frase
    // aproximada sobre quien ve que.
    description: leerTexto(item, 'text', 'description', 'explanation'),
    // El backend lo manda como `scope: "global" | "group"`, no como booleano.
    global: leerTexto(item, 'scope') === 'global' || leerBooleano(item, 'global', 'is_global'),
    unrestricted: leerBooleano(item, 'unrestricted'),
    domain: leerDominio(item),
  };
}

function normalizarAlcances(crudo: Record<string, unknown>): RoleScope[] {
  const lista = leerLista(crudo, 'record_scope', 'scopes', 'record_scopes', 'record_rules', 'rules');
  return lista.map(normalizarAlcance).filter((a): a is RoleScope => a !== null);
}

/**
 * Lee `record_scope_by_model`. Si el backend no lo manda, agrupa la lista plana
 * por modelo con los mismos criterios (globales aparte de las del rol) para que
 * la pantalla nunca vuelva a mostrar dos reglas seguidas sin conector.
 */
function normalizarGruposDeAlcance(
  crudo: Record<string, unknown>,
  planas: RoleScope[],
): RoleScopeGroup[] {
  const lista = leerLista(crudo, 'record_scope_by_model');
  if (lista.length > 0) {
    const grupos: RoleScopeGroup[] = [];
    for (const item of lista) {
      if (!esObjeto(item)) continue;
      const model = leerTexto(item, 'model', 'model_name');
      if (model === '') continue;
      const label = leerTexto(item, 'label', 'model_label');
      grupos.push({
        model,
        label: label === '' ? model : label,
        hard: leerLista(item, 'hard')
          .map(normalizarAlcance)
          .filter((a): a is RoleScope => a !== null),
        group: leerLista(item, 'group')
          .map(normalizarAlcance)
          .filter((a): a is RoleScope => a !== null),
        hardTitle: leerTexto(item, 'hard_title'),
        groupTitle: leerTexto(item, 'group_title'),
        effect: leerTexto(item, 'effect'),
      });
    }
    if (grupos.length > 0) return grupos;
  }

  const porModelo = new Map<string, RoleScopeGroup>();
  for (const alcance of planas) {
    let grupo = porModelo.get(alcance.model);
    if (grupo === undefined) {
      grupo = {
        model: alcance.model,
        label: alcance.label,
        hard: [],
        group: [],
        // Sin dato del servidor no se inventa un conector: se usan los rotulos
        // neutros y no se afirma nada sobre como se combinan.
        hardTitle: '',
        groupTitle: '',
        effect: '',
      };
      porModelo.set(alcance.model, grupo);
    }
    (alcance.global ? grupo.hard : grupo.group).push(alcance);
  }
  return [...porModelo.values()];
}

function normalizarRol(crudo: unknown): Role | null {
  if (!esObjeto(crudo)) return null;
  const code = leerTexto(crudo, 'code', 'name');
  if (code === '') return null;

  const name = leerTexto(crudo, 'name', 'label', 'title');
  const id = leerNumero(crudo, 'id', 'pk', 'group_id');
  const scopes = normalizarAlcances(crudo);

  return {
    id,
    code,
    // Si `name` es igual al code (porque solo vino `name`), se muestra el code:
    // es preferible un identificador honesto a una etiqueta fabricada.
    name: name === '' ? code : name,
    description: leerTexto(crudo, 'description', 'help', 'summary'),
    module: (() => {
      const modulo = leerTexto(crudo, 'module', 'app');
      return modulo === '' ? (code.includes('.') ? moduloDeModelo(code) : null) : modulo;
    })(),
    userCount: leerNumero(crudo, 'user_count', 'users_count', 'members', 'users'),
    areas: normalizarAreas(crudo),
    scopes,
    scopeGroups: normalizarGruposDeAlcance(crudo, scopes),
    bypassNote: leerTexto(crudo, 'bypass_note'),
  };
}

/**
 * Convierte la respuesta cruda del endpoint en la lista de roles del panel.
 *
 * @throws {ContractError} si el cuerpo no contiene nada parecido a una lista de
 * roles. Preferimos fallar con un mensaje explicito antes que dibujar una
 * pantalla vacia que parezca "no hay roles configurados".
 */
export function normalizarRoles(cuerpo: unknown): Role[] {
  let lista: unknown[] | null = null;

  if (Array.isArray(cuerpo)) {
    lista = cuerpo;
  } else if (esObjeto(cuerpo)) {
    for (const clave of ['roles', 'results', 'items', 'data']) {
      const valor = cuerpo[clave];
      if (Array.isArray(valor)) {
        lista = valor;
        break;
      }
    }
  }

  if (lista === null) {
    throw new ContractError(
      'El servidor respondió algo que no es una lista de roles. Se esperaba ' +
        '`{"roles": [...]}` en /api/v1/admin/roles/.',
    );
  }

  const roles = lista.map(normalizarRol).filter((rol): rol is Role => rol !== null);
  if (roles.length === 0 && lista.length > 0) {
    throw new ContractError(
      'El servidor devolvió roles sin código identificable. Cada rol necesita al menos `code`.',
    );
  }
  return roles.sort((a, b) => a.name.localeCompare(b.name, 'es'));
}

// ---------------------------------------------------------------------------
// Derivaciones que usa la UI
// ---------------------------------------------------------------------------
/** `true` si el rol concede al menos una operacion sobre el modelo. */
export function concedeAlgo(capacidad: RoleCapability): boolean {
  return capacidad.create || capacidad.read || capacidad.write || capacidad.delete;
}

/** Los verbos concedidos por una capacidad, en orden de riesgo. */
export function verbosConcedidos(capacidad: RoleCapability): CrudVerb[] {
  return VERBOS.filter((verbo) => capacidad[verbo]);
}

/** Los verbos NEGADOS por una capacidad. */
export function verbosNegados(capacidad: RoleCapability): CrudVerb[] {
  return VERBOS.filter((verbo) => !capacidad[verbo]);
}

/** Todas las capacidades de un rol, aplanadas, con el area a la que pertenecen. */
export function capacidadesDe(rol: Role): { area: RoleArea; capability: RoleCapability }[] {
  return rol.areas.flatMap((area) => area.capabilities.map((capability) => ({ area, capability })));
}

/** Indice modelo -> capacidad, para comparar dos roles sin recorrer listas anidadas. */
export function indicePorModelo(rol: Role): Map<string, RoleCapability> {
  const indice = new Map<string, RoleCapability>();
  for (const { capability } of capacidadesDe(rol)) indice.set(capability.model, capability);
  return indice;
}

/**
 * Universo de modelos que el sistema conoce, sacado de TODOS los roles.
 *
 * Es lo que permite decir "que NO puede hacer": un modelo que otros roles tocan
 * y este no. Sale de datos reales del endpoint, no de una lista escrita a mano.
 */
export function universoDeModelos(roles: Role[]): Map<string, { label: string; area: string }> {
  const universo = new Map<string, { label: string; area: string }>();
  for (const rol of roles) {
    for (const { area, capability } of capacidadesDe(rol)) {
      const existente = universo.get(capability.model);
      if (existente === undefined || (existente.label === capability.model && !capability.labelIsTechnical)) {
        universo.set(capability.model, { label: capability.label, area: area.key });
      }
    }
  }
  return universo;
}

/** Etiquetas de area vistas en la respuesta, para no volver a buscarlas. */
export function etiquetasDeArea(roles: Role[]): Map<string, string> {
  const etiquetas = new Map<string, string>();
  for (const rol of roles) {
    for (const area of rol.areas) {
      if (area.label !== '' && !etiquetas.has(area.key)) etiquetas.set(area.key, area.label);
    }
  }
  return etiquetas;
}
